import { useAtom } from 'jotai';

import { projectIdAtom } from '~/atoms/project';
import OfflineInformer from '~/components/OfflineInformer';
import UpdateNotifier from '~/components/UpdateNotifier';
import Toast from '~/components/ui/Toast';
import { APP_STATES, useAppState } from '~/hooks/useAppState';
import useDevice from '~/hooks/useDevice';
import useDroppedFile from '~/hooks/useDroppedFile';
import AppStateDisplay from './AppStateDisplay';
import EPToolkitDialog from './EPToolkitDialog';
import FacePlateHeader from './FacePlateHeader';
import FeedbackDialog from './FeedbackDialog';
import Menu from './Menu';
import ProjectMeta from './ProjectMeta';
import Arrangements from './ProjectView/Arrangements';
import RiddimDialog from './RiddimDialog';

function Home() {
  const [projectId] = useAtom(projectIdAtom);
  const { error } = useDevice();
  const appState = useAppState();
  const { dropRef } = useDroppedFile();

  return (
    <div className="min-w-275 max-w-450 p-4 h-screen grid grid-rows-[auto_1fr] mx-auto">
      <div className="mb-2 empty:mb-0">
        <UpdateNotifier />
        <OfflineInformer />
      </div>

      <div className="grid grid-rows-[auto_1fr_auto] gap-2 max-h-full min-h-0">
        <header className="flex flex-col gap-2">
          <div className="flex justify-between">
            <FacePlateHeader />

            <div className="self-start flex flex-col gap-2">
              <Menu />
              <a
                href="https://eptoolkit.ep133-to-daw.cc/"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-creme bg-grid border border-black shadow-[1px_1px_0px_1px_#00000099] flex items-center gap-2 h-15 px-3"
              >
                <div className="flex flex-col justify-center min-w-0">
                  <span className="text-2xl font-bold leading-tight text-brand">EP Toolkit</span>
                  <span className="text-xs text-black/60 leading-tight">
                    Standalone app: export, samples, backups
                  </span>
                </div>
                <img
                  src="/eptoolkit/eptoolkit-logo.png"
                  alt="EP Toolkit"
                  className="size-10 shrink-0 ml-auto"
                />
              </a>
            </div>
          </div>
          <ProjectMeta projectId={projectId} />
        </header>

        <main
          ref={dropRef as any}
          className="bg-white h-full border p-4 shadow-[1px_1px_0px_1px_#00000099] overflow-scroll"
        >
          {appState.includes(APP_STATES.CAN_DISPLAY_PROJECT) && (
            <Arrangements projectId={projectId} />
          )}

          {appState.includes(APP_STATES.LOADING) && (
            <AppStateDisplay
              title="Loading..."
              message="Please wait while the project is loading."
            />
          )}

          {appState.includes(APP_STATES.NO_PROJECT_SELECTED) && (
            <AppStateDisplay
              title="Ready to Export!"
              message="Select a project from the dropdown."
            />
          )}

          {appState.includes(APP_STATES.NO_DEVICE_CONNECTED) && (
            <AppStateDisplay
              title="No Device Connected"
              message={
                <>
                  Please connect a device and allow access to MIDI
                  <br />
                  or drop a .pak/.ppak backup file
                </>
              }
            />
          )}

          {appState.includes(APP_STATES.ERROR) && error && (
            <AppStateDisplay title="Error" message={error?.message || 'Unknown error'} />
          )}
        </main>
        <div className="bg-face px-3 py-2 border border-black text-xs text-black/70 shadow-[1px_1px_0px_1px_#00000099] flex items-center justify-between">
          <span>
            This project is not affiliated with or officially authorized by Teenage Engineering
          </span>
          <a
            href="https://randomware-audio.cc/"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 text-black text-sm"
          >
            <img src="/ra-logo.svg" alt="" className="size-5" />
            Randomware Audio
          </a>
        </div>
      </div>

      <FeedbackDialog />
      <EPToolkitDialog />
      <RiddimDialog />
      <Toast />
    </div>
  );
}

export default Home;
